'use client';
import React from 'react';
import Link from '@/components/LocaleLink';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useSessionContext, signOut } from 'supertokens-auth-react/recipe/session';
import { REGIONS } from '@/utils/regions';
import useLocalePath from '@/hooks/useLocalePath';
import { useTranslation } from '@/contexts/TranslationContext';
// Dynamic imports with proper handling of default exports
// icons
const KeyboardArrowDownIcon = dynamic(() => import('@mui/icons-material/KeyboardArrowDown'), { ssr: false });
const CustomDropdown = dynamic(() => import('../components/customDropdown'), { ssr: false });

const Header = () => {
  const location = useRouter();
  const session = useSessionContext();
  const pathname = usePathname();
  const localePath = useLocalePath();
  const { t } = useTranslation();

  const showMobilemenu = () => {
    document.getElementById('sidebarArea').classList.toggle('showSidebar');
  };

  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [chartsDropDown, setchartsDropDown] = React.useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const toggleChartDropdown = () => setchartsDropDown((prevState) => !prevState);
  const [isOpen, setIsOpen] = React.useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const logout = () => {
    if (typeof window !== 'undefined') {
      signOut();
      window.localStorage.removeItem('lm_main_auth');
      window?.sessionStorage.setItem('member_id', null);
      location.push(localePath('/'));
    }
  };

  const isHome = (() => {
    if (pathname === '/') return true;
    // Locale root paths like /de, /uk, /fr are also the homepage
    const localeRoots = REGIONS.filter(r => r.prefix).map(r => r.prefix);
    if (localeRoots.includes(pathname)) return true;
    return false;
  })();

  // After login, redirect back to the page user was on (when they clicked Log In)
  const signinHref = pathname === '/signin' ? '/signin' : `/signin?returnUrl=${encodeURIComponent(pathname || '/')}`;

  if (!isHome) {
    return (
      <HomeNavbar
        logout={logout}
        toggle={toggle}
        dropdownOpen={dropdownOpen}
        showMobilemenu={showMobilemenu}
        session={session}
        location={location}
        signinHref={signinHref}
      />
    );
  }
  return (
    <div>
      <nav className="navbar navbar-expand-lg d-none d-lg-block  fixed-top" style={{ zIndex: 4 }}>
        <div className="container">
          <div className="d-flex align-items-center d-lg-none">
            <button
              aria-label={t('aria.openMenu')}
              className="btn d-lg-none"
              style={{ color: '#fff' }}
              onClick={() => showMobilemenu()}
            >
              <span className="mobile-nav">
                <Image
                  src="/assets/images/icons/menu.svg"
                  className="img-fluid rounded-top"
                  alt={t('aria.menuIcon')}
                  width="19"
                  height="12"
                  priority
                />
              </span>
            </button>
          </div>

          {isHome && (
            <>
              <div className={`dropdown chart-dropdown ${chartsDropDown ? 'show' : ''}`}>
                <button style={{ color: '#fff' }} className="btn top-nav-link " onClick={toggleChartDropdown}>
                  <span style={{ fontWeight: '400' }}>{t('nav.charts')}</span>
                  <KeyboardArrowDownIcon />
                </button>
                <ul
                  className={`dropdown-menu fade-up ${chartsDropDown ? 'show' : ''}`}
                  style={{ borderRadius: '10px' }}
                >
                  <button className="dropdown-item" onClick={() => location.push(localePath('/net-worth'))}>
                    {t('nav.netWorth')}
                  </button>
                  <button className="dropdown-item" onClick={() => location.push(localePath('/annual-income'))}>
                    {t('nav.annualIncome')}
                  </button>
                  <button className="dropdown-item" onClick={() => location.push(localePath('/retirement-fund'))}>
                    {t('nav.retirementFund')}
                  </button>
                </ul>
              </div>

              <Link className="nav-link top-nav-link" href="/calculators">
                {t('nav.calculators')}
              </Link>

              <Link className="nav-link" prefetch={false} href="/">
                <Image
                  priority
                  className="mainLogo"
                  src="/assets/images/logos/NW-Logo.svg"
                  alt="LM"
                  width="300"
                  height="48"
                />
              </Link>

              <Link className="nav-link top-nav-link" href="/info">
                {t('nav.articles')}
              </Link>
            </>
          )}

          {!session.doesSessionExist && (
            <Link prefetch={false} className="nav-link top-nav-link" href={signinHref}>
              <div className="signinup">
                <Image priority src="/assets/images/section2Icons/profile.svg" alt={t('header.loginAlt')} height="24" width="24" />
                <span className="ms-1 d-inline-block">{t('nav.logInSignUp')}</span>
              </div>
            </Link>
          )}

          {session.doesSessionExist && (
            <div className={`dropdown ${dropdownOpen ? 'show' : ''}`}>
              <button
                className="btn p-0 profile-btn dropdown-toggle"
                style={{ backgroundColor: 'transparent' }}
                onClick={toggle}
              >
                <div className="memberprofile">
                  <svg
                    className="me-1"
                    width="22"
                    height="23"
                    viewBox="0 0 22 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.0013 11.2634C13.5326 11.2634 15.5846 9.21138 15.5846 6.68007C15.5846 4.14877 13.5326 2.09674 11.0013 2.09674C8.47 2.09674 6.41797 4.14877 6.41797 6.68007C6.41797 9.21138 8.47 11.2634 11.0013 11.2634Z"
                      stroke="#fff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.8733 20.4301C18.8733 16.8826 15.3442 14.0134 10.9992 14.0134C6.65416 14.0134 3.125 16.8826 3.125 20.4301"
                      stroke="#fff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t('nav.profile')}
                </div>
              </button>
              <ul className={`dropdown-menu fade-up ${dropdownOpen ? 'show' : ''}`} style={{ borderRadius: '10px' }}>
                <button className="dropdown-item" onClick={() => location.push(localePath('/profile'))}>
                  {t('nav.profile')}
                </button>
                <button className="dropdown-item" onClick={() => location.push(localePath('/members/view'))}>
                  {t('nav.myMeasurements')}
                </button>
                <button className="dropdown-item" onClick={logout}>
                  {t('nav.logout')}
                </button>
              </ul>
            </div>
          )}
        </div>
      </nav>

      {/* mobile view */}
      <nav
        className="navbar d-lg-none"
        style={{ zIndex: 3, width: '100%', backgroundColor: '#25AF9B', minHeight: '60px' }}
      >
        <div className="container d-flex align-items-center justify-content-between">
          {/* Hamburger Menu Button */}
          <button
            aria-label={t('aria.openMenu')}
            className="btn d-lg-none p-0"
            style={{ color: '#fff' }}
            onClick={() => showMobilemenu()}
          >
            <span className="mobile-nav">
              <Image
                priority
                src="/assets/images/icons/menu.svg"
                className="img-fluid rounded-top"
                alt={t('aria.menuIcon')}
                width="19"
                height="12"
              />
            </span>
          </button>

          {/* Logo */}
          <Link className="nav-link" prefetch={false} href="/">
            <Image
              height="20"
              width="300"
              className="img-fluid"
              style={{ maxWidth: '170px' }}
              src="/assets/images/logos/NW-Logo.svg"
              alt="LM"
              priority
            />
          </Link>

          {/* Log In/Sign Up Link (visible when session doesn't exist) */}
          {!session.doesSessionExist && (
           <Link href={signinHref} className="nav-link top-nav-link">
              <div className="signinup">
                <Image src="/assets/images/section2Icons/profile.svg" alt={t('header.loginAlt')} height="24" width="24" />
                <span className="ms-1 d-none d-md-inline-block">{t('nav.logInSignUp')}</span>
              </div>
            </Link>
          )}

          {/* Profile Dropdown (visible when session exists) */}
          {session.doesSessionExist && (
            <div className="dropdown">
              <button
                className="btn dropdown-toggle p-0 profile-btn"
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                // data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <Image src="/assets/images/section2Icons/profile.svg" alt={t('header.profileAlt')} height="24" width="24" />
              </button>
              <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                <button className="dropdown-item" onClick={() => location.push(localePath('/profile'))}>
                  {t('nav.profile')}
                </button>
                <button className="dropdown-item" onClick={() => location.push(localePath('/members/view'))}>
                  {t('nav.myMeasurements')}
                </button>
                <button className="dropdown-item" onClick={logout}>
                  {t('nav.logout')}
                </button>
              </ul>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Header;

export const HomeNavbar = ({ isHome, dropdownOpen, toggle, logout, showMobilemenu, session, location, signinHref = '/signin' }) => {
  const localePath = useLocalePath();
  const { t } = useTranslation();
  return (
    <div className="navbar-wrapper">
      <nav className="d-none d-lg-block p-2 secondary-nav" style={{ zIndex: 4, width: '100%', minHeight: '50px' }}>
        <div className="container">
          <div className="center-nav d-flex align-items-center">
            <div></div>
            {/* Logo Section */}
            <Link prefetch={false} className="nav-link" href="/">
              <Image
                src="/assets/images/logos/logo-dark-text.svg"
                alt="LM"
                width="300"
                height="48"
                className="mainLogo img-fluid"
                loading="lazy"
              />
            </Link>

            {/* Log In / Sign Up */}
            {!session.doesSessionExist && (
              <Link href={signinHref} className="nav-link top-nav-link pos-corner-right">
                <div className="signinup d-flex align-items-center">
                  <Image src="/assets/images/icons/user.svg" alt={t('header.loginAlt')} height="30" width="30" className="me-1" />
                  <span className="ms-1 d-none d-md-inline-block dark-text">{t('nav.logInSignUp')}</span>
                </div>
              </Link>
            )}

            {/* Profile Dropdown (if session exists) */}
            {session.doesSessionExist && (
              // aria-expanded={dropdownOpen}
              <div className="dropdown navbar-right pos-corner-right">
                <button
                  className="btn p-0 profile-btn dropdown-toggle"
                  style={{ backgroundColor: 'transparent' }}
                  onClick={toggle}
                >
                  <div className="memberprofile text-dark d-flex align-items-center">
                    <svg
                      className="me-1"
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.0013 11.2634C13.5326 11.2634 15.5846 9.21138 15.5846 6.68007C15.5846 4.14877 13.5326 2.09674 11.0013 2.09674C8.47 2.09674 6.41797 4.14877 6.41797 6.68007C6.41797 9.21138 8.47 11.2634 11.0013 11.2634Z"
                        stroke="#747474"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.8733 20.4301C18.8733 16.8826 15.3442 14.0134 10.9992 14.0134C6.65416 14.0134 3.125 16.8826 3.125 20.4301"
                        stroke="#747474"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {t('nav.profile')}
                    <KeyboardArrowDownIcon />
                  </div>
                </button>
                <ul
                  className={`dropdown-menu fade-up ${dropdownOpen ? 'show' : ''}`}
                  aria-labelledby="dropdownMenuButton"
                  style={{ borderRadius: '10px' }}
                >
                  <button className="dropdown-item" onClick={() => location.push(localePath('/profile'))}>
                    {t('nav.profile')}
                  </button>
                  <button className="dropdown-item" onClick={() => location.push(localePath('/members/view'))}>
                    {t('nav.myMeasurements')}
                  </button>
                  <button className="dropdown-item" onClick={signOut}>
                    {t('nav.logout')}
                  </button>
                </ul>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* mobile screen */}
      <nav className="navbar d-lg-none" style={{ zIndex: 3, width: '100%', backgroundColor: '#25AF9B' }}>
        <div className="container d-flex align-items-center justify-content-between w-100">
          {/* Hamburger Menu Button */}
          <button aria-label={t('aria.openMenu')} className="btn p-0" style={{ color: '#fff' }} onClick={() => showMobilemenu()}>
            <span className="mobile-nav">
              <Image
                src="/assets/images/icons/menu.svg"
                className="img-fluid rounded-top"
                alt={t('aria.menuIcon')}
                width="19"
                height="12"
              />
            </span>
          </button>

          {/* Logo */}
          <Link className="nav-link" prefetch={false} href="/">
            {/* <Image
              priority
              fetchPriority="high"
              className="img-fluid"
              style={{ maxWidth: '170px' }}
              alt="LM"
              height="32"
              width="170"
              src="https://res.cloudinary.com/hgiulssdc/image/upload/chart_images/M-LM-logo.svg"
            /> */}
            <Image
              priority
              fetchPriority="high"
              className="img-fluid"
              style={{ maxWidth: '170px' }}
              alt="LM"
              height="33"
              width="170"
              src="/assets/images/logos/NW-Logo.svg"
            />
          </Link>

          {/* Log In / Sign Up Link (when session does not exist) */}
          {!session.doesSessionExist && (
            <Link href={signinHref} className="nav-link top-nav-link">
              <div className="signinup d-flex align-items-center">
                <Image src="/assets/images/section2Icons/profile.svg" alt={t('header.loginAlt')} height="24" width="24" />
                <span className="ms-1 d-none d-md-inline-block">{t('nav.logInSignUp')}</span>
              </div>
            </Link>
          )}

          {/* Profile Dropdown (when session exists) */}
          {session.doesSessionExist && <CustomDropdown logout={logout} />}
        </div>
      </nav>
    </div>
  );
};
